import React, { useEffect, useState } from "react";
import { Download, X, Share, PlusSquare } from "lucide-react";

const DISMISS_KEY = "plash_pwa_install_dismissed";

function isIos() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent) ||
    // iPadOS 13+ reports as Mac; detect touch + Safari
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

function isIosSafari() {
  const ua = window.navigator.userAgent;
  return isIos() && !/CriOS|FxiOS|EdgiOS|OPiOS|mercury/i.test(ua);
}

function isInStandaloneMode() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

export const InstallPWA = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIosHelp, setShowIosHelp] = useState(false);

  useEffect(() => {
    if (isInStandaloneMode()) return;
    if (localStorage.getItem(DISMISS_KEY) === "1") return;

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // iOS Safari has no beforeinstallprompt; show manual instructions
    if (isIos()) setVisible(true);

    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const dismiss = () => {
    setVisible(false);
    setShowIosHelp(false);
    localStorage.setItem(DISMISS_KEY, "1");
  };

  const handleInstall = async () => {
    if (isIos() && !deferredPrompt) {
      setShowIosHelp(true);
      return;
    }
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") {
      setVisible(false);
      localStorage.setItem(DISMISS_KEY, "1");
    }
    setDeferredPrompt(null);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="pwa-install-banner"
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-80 z-[9998] rounded-2xl bg-white dark:bg-slate-800 shadow-xl border border-slate-200 dark:border-slate-700 p-4"
    >
      <button
        data-testid="pwa-install-dismiss"
        onClick={dismiss}
        aria-label="Dismiss"
        className="absolute top-2 right-2 text-slate-400 dark:text-slate-500 hover:text-slate-600"
      >
        <X size={18} />
      </button>
      <div className="flex items-start gap-3 pr-4">
        <div className="flex-shrink-0 w-11 h-11 rounded-xl overflow-hidden">
          <img src="/logo192.png" alt="Plash" className="w-full h-full object-cover" />
        </div>
        <div>
          <p className="font-semibold text-slate-900 dark:text-slate-100 text-sm">Install Plash Attendance</p>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            Add to your home screen for quick, app-like access.
          </p>
        </div>
      </div>

      {showIosHelp ? (
        isIosSafari() ? (
          <div className="mt-3 space-y-2" data-testid="ios-install-help">
            <p className="text-xs text-slate-600 dark:text-slate-400">Install on your iPhone/iPad in 2 steps:</p>
            <div className="flex items-center gap-2 text-xs text-slate-700 dark:text-slate-300">
              <span className="flex items-center justify-center w-5 h-5 rounded-full bg-blue-600 text-white text-[10px] font-bold flex-shrink-0">1</span>
              Tap the <Share size={14} className="inline mx-0.5 text-blue-600" /> <span className="font-medium">Share</span> button in Safari's toolbar
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-700 dark:text-slate-300">
              <span className="flex items-center justify-center w-5 h-5 rounded-full bg-blue-600 text-white text-[10px] font-bold flex-shrink-0">2</span>
              Choose <PlusSquare size={14} className="inline mx-0.5 text-blue-600" /> <span className="font-medium">“Add to Home Screen”</span>, then tap Add
            </div>
          </div>
        ) : (
          <p className="mt-3 text-xs text-amber-700 dark:text-amber-400 leading-relaxed bg-amber-50 dark:bg-amber-900/20 rounded-lg p-2.5" data-testid="ios-safari-required">
            On iPhone/iPad, apps can only be installed from <span className="font-semibold">Safari</span>. Please open this page in Safari, then tap Share → “Add to Home Screen”.
          </p>
        )
      ) : (
        <button
          data-testid="pwa-install-button"
          onClick={handleInstall}
          className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium py-2.5 transition-colors"
        >
          <Download size={16} />
          Install App
        </button>
      )}
    </div>
  );
};

export default InstallPWA;
